import DockingPluginBase from './DockingPluginBase';
import calculateDocking from '../actions/calculateDocking';
import DockingViewState from '../store/schema/DockingViewState';
import { PluginViewStateAdapter } from 'owa-editor';
import { lazySubscribeToResizeEvent, lazyUnsubscribeFromResizeEvent } from 'owa-resize-event';
import { Editor } from 'roosterjs-editor-core';

export default class DockingPlugin extends DockingPluginBase {
    private observer: MutationObserver;
    private observerConnected: boolean;
    private dockingThrottlingJob: number;

    constructor(adapter: PluginViewStateAdapter<DockingViewState>) {
        super(adapter);

        if (typeof MutationObserver == 'function') {
            this.observer = new MutationObserver(this.calculateDockingWithThrottling);
        }
    }

    public getName() {
        return 'Docking';
    }

    public initialize(editor: Editor) {
        super.initialize(editor);

        this.scrollAdapter.addScrollEventListener(this.calculateDockingWithThrottling);
        this.tryStartObserver();
        lazySubscribeToResizeEvent.importAndExecute(this.calculateDockingWithThrottling);
    }

    public dispose() {
        lazyUnsubscribeFromResizeEvent.importAndExecute(this.calculateDockingWithThrottling);
        this.tryStopObserver();
        this.scrollAdapter.removeScrollEventListener(this.calculateDockingWithThrottling);

        if (this.dockingThrottlingJob) {
            window.cancelAnimationFrame(this.dockingThrottlingJob);
            this.dockingThrottlingJob = 0;
        }

        super.dispose();
    }

    protected onScrollContainerChanged = (scrollContainer: HTMLDivElement) => {
        if (scrollContainer) {
            this.tryStartObserver();
        } else {
            this.tryStopObserver();
        }
    };

    private tryStartObserver() {
        const scrollContainer = this.scrollAdapter.getScrollContainer();

        // TODO: Once we support compose projection window, need to make sure the observer code works well in projection window
        // We can check this.viewState.isPopout to know if this is in popout window
        if (this.observer && !this.observerConnected && this.editor && scrollContainer) {
            this.observer.observe(scrollContainer, {
                childList: true,
                attributes: true,
                subtree: true,
            });

            this.tryScrollIntoView();
            // Do an initial docking calculation
            this.calculateDockingWithThrottling();
            this.observerConnected = true;
        }
    }

    private tryStopObserver() {
        if (this.observer && this.observerConnected) {
            this.observer.disconnect();
            this.observerConnected = false;
        }
    }

    private calculateDockingWithThrottling = () => {
        if (!this.dockingThrottlingJob) {
            this.dockingThrottlingJob = window.requestAnimationFrame(() => {
                this.dockingThrottlingJob = 0;
                // Check if the editor is still before starting the docking calculation
                if (this.editor) {
                    this.onCalculateDocking();
                }
            });
        }
    };

    private onCalculateDocking = () => {
        const scrollContainer = this.scrollAdapter.getScrollContainer();
        const viewState = this.adapter();
        if (viewState && scrollContainer && this.dockingTriggerPart) {
            const newWidth = this.dockingTriggerPart.offsetWidth;
            calculateDocking(
                viewState,
                scrollContainer.scrollTop,
                scrollContainer.scrollLeft,
                scrollContainer.clientHeight,
                scrollContainer.offsetHeight,
                this.dockingTriggerPart.offsetTop,
                this.dockingTriggerPart.offsetHeight,
                newWidth
            );
        }
    };
}
